import React, { cloneElement } from 'react';
import PropTypes from 'prop-types';
import isRequiredIf from '../utils/isRequiredIf';
import FormLabel from './FormLabel';
import { FormElementMode } from './PropTypes';
import { resolve, select } from '@cvent/nucleus-dynamic-css';
import removeKeys from '../utils/removeKeys';
import ErrorMessages from './ErrorMessages';
import FormElementClasses from './FormElement.less';
import { injectTestId, resolveTestId } from '@cvent/nucleus-test-automation';

/**
A wrapper to use for different form element components (select, textbox, etc) to add
common functionality like labels, required field indicators, etc.
**/
class FormElement extends React.Component {
  static displayName = 'FormElement';
  static propTypes = {
    /**
      Options are 'view' and 'edit'; default set to 'edit';
      When set to 'view', input will be rendered as passed in 'view' prop instead
    */
    mode: FormElementMode,
    /** A react component to render as the view for the form element input when it is in view mode. */
    view: PropTypes.node,
    /**
      The id of the form element, also gets passed to label to link the label to the element.
      Defaults to fieldName if not provided.
    */
    id: PropTypes.string,
    /** The name of the form element. Defaults to fieldName if not provided. */
    name: PropTypes.string,
    /**
      Should be unique, as it's used to tie label and input together,
      and also used by onChange callback to identify which input is changed.
    */
    fieldName: PropTypes.string.isRequired,
    /** Label text for the form element. */
    label: PropTypes.node,
    /** Additional text for the form element. */
    additionalLabelText: PropTypes.node,
    /** Flag indicating whether to hide the label text. */
    hideLabel: PropTypes.bool,
    /** Flag indicating whether to show the additional label text in view mode. */
    showAdditionalLabelTextInViewMode: PropTypes.bool,
    /** Flag indicating whether to render as a fieldset. */
    isFieldSet: PropTypes.bool,
    /** Flag indicating whether the field is required. */
    required: PropTypes.bool,
    /** A react component to display as a tooltip for the form element. */
    tooltip: PropTypes.node,
    /** Callback Function. Should be implemented per form element. Should exist if onNativeChange is not provided. */
    onChange: isRequiredIf(
      PropTypes.func,
      props => !props.hasOwnProperty('onNativeChange')
    ),
    /** Callback Function for react-redux-form support. Should exist if onChange is not provided. */
    onNativeChange: isRequiredIf(
      PropTypes.func,
      props => !props.hasOwnProperty('onChange')
    ),
    /** Callback Function. Can be implemented per form element. */
    onClick: PropTypes.func,
    /** Callback Function. Can be implemented per form element. */
    onKeyDown: PropTypes.func,
    /** Callback Function. Can be implemented per form element. */
    onBlur: PropTypes.func,
    /** Callback Function. Can be implemented per form element. */
    onFocus: PropTypes.func,
    /** Can be implemented per form element to set focus on mount */
    setFocus: PropTypes.bool,
    /**  Messages to display with the form element indicating there are errors.
     If not present it is assumed the input is valid **/
    errorMessages: PropTypes.oneOfType([
      PropTypes.objectOf(PropTypes.string),
      PropTypes.arrayOf(PropTypes.string)
    ]),
    /** The child content to render in the form element. */
    children: PropTypes.node,
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Top level style applied to the container. */
      element: PropTypes.object,
      /** Style applied to the wrapper around the child content. */
      inputContainer: PropTypes.object,
      /** Style applied to the FormLabel component. */
      label: PropTypes.object,
      /** Style to apply if in view mode. */
      view: PropTypes.object,
      /** Style to apply to the tooltip content. */
      tooltip: PropTypes.object
    })
  };

  static defaultProps = {
    mode: 'edit',
    view: null,
    required: false,
    hideLabel: false,
    showAdditionalLabelTextInViewMode: false,
    classes: {}
  };

  state = {
    key: undefined
  };

  /**
   * Updating the value for CSS float property on <legend> tag after this component is mounted
   * causes the <legend> tag disappears or overlaps with the content below it in Safari.
   * The fix is to update the key on this component when the float value is changed, either through classes or style,
   * to force a full re-instantiation of this component.
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { isFieldSet, mode } = nextProps;
    const renderAsFieldSet = isFieldSet && mode === 'edit';
    if (!renderAsFieldSet) {
      return;
    }

    let newKey;
    const classObject = this.getLabelStyle(this.props);
    const nextClassObject = this.getLabelStyle(nextProps);
    if (nextClassObject.classes.label !== classObject.classes.label) {
      newKey = nextClassObject.classes.label;
    }
    if (
      (nextClassObject.style.label &&
      classObject.style.label &&
      nextClassObject.style.label.float !== classObject.style.label.float) ||
      (nextClassObject.style.label && !classObject.style.label && nextClassObject.style.label.float)
    ) {
      newKey = newKey
        ? `${newKey}-${nextClassObject.style.label.float}`
        : nextClassObject.style.label.float;
    } else if (!nextClassObject.style.label && classObject.style.label && classObject.style.label.float) {
      newKey = newKey
        ? `${newKey}-no-inline-float`
        : 'no-inline-float';
    }
    if (newKey) {
      this.setState({
        key: `${this.state.fieldName}-${newKey}`
      });
    }
  }

  getLabelStyle = props => {
    // Shallow Labels out if they exist in nested form //
    const labelStyle = select(props, 'label');
    return {
      classes: {
        ...props.classes,
        ...labelStyle.classes
      },
      style: {
        ...props.style,
        ...labelStyle.style
      }
    };
  };
  render() {
    const {
      mode,
      view,
      id,
      fieldName,
      required,
      tooltip,
      label,
      additionalLabelText,
      hideLabel,
      showAdditionalLabelTextInViewMode,
      errorMessages,
      children,
      isFieldSet
    } = this.props;

    // Determine if there is a view that can be rendered.
    const viewToRender =
      mode === 'view' && view
        ? cloneElement(view, resolve(this.props, 'view'))
        : null;

    // Determine whether to render the view component or the children.
    const elementToRender =
      mode === 'view' && viewToRender ? viewToRender : children;

    const renderAsFieldSet = isFieldSet && mode === 'edit';
    const WrapperTag = renderAsFieldSet ? 'fieldset' : 'div';

    const resolvedStyles = resolve(this.props, 'element');
    const wrapperStyle = resolvedStyles.style;
    const classObject = this.getLabelStyle(this.props);
    const errorsPresent = Array.isArray(errorMessages)
      ? errorMessages.length > 0
      : Object.keys(errorMessages || {}).length > 0;
    const className = errorsPresent
      ? `${resolvedStyles.className} ${
        FormElementClasses.formElementWithErrors
      }`
      : resolvedStyles.className;
    return (
      <WrapperTag
        className={className}
        style={wrapperStyle}
        {...resolveTestId(this.props)}
        key={this.state.key}
      >
        <FormLabel
          mode={mode}
          label={label}
          additionalLabelText={additionalLabelText}
          hideLabel={hideLabel}
          showAdditionalLabelTextInViewMode={showAdditionalLabelTextInViewMode}
          fieldId={id || fieldName}
          required={required}
          element={renderAsFieldSet ? 'legend' : 'label'}
          classes={classObject.classes}
          style={classObject.style}
          {...injectTestId('label')}
        />
        <div {...resolve(this.props, 'inputContainer')}>
          {elementToRender}
          {errorsPresent && (
            <ErrorMessages
              {...select(this.props, 'errorMessages')}
              {...injectTestId('error-messages')}
              errorMessages={errorMessages}
            />
          )}
        </div>
        {tooltip ? cloneElement(tooltip, resolve(this.props, 'tooltip')) : null}
      </WrapperTag>
    );
  }
}

export default FormElement;

/**
 * Removes any FormElement specific properties from a props object that would be invalid
 * if applied to a base DOM node. Useful when composing multiple components to prevent
 * passing a component props that it doesn't use.
 */
export function removeNonStandardProps(props) {
  return removeKeys(props, [
    'mode',
    'view',
    'fieldName',
    'label',
    'additionalLabelText',
    'hideLabel',
    'tooltip',
    'setFocus',
    'classes',
    'onNativeChange',
    'selectedValue',
    'selectedValues',
    'errorMessages',
    'showAdditionalLabelTextInViewMode',
    'formatMask'
  ]);
}
